import {
  z,
  createValidationSchema,
  createEmailSchema,
  createPasswordSchema,
  strongPasswordSchema,
  PASSWORD_MIN_LENGTH,
  PASSWORD_MAX_LENGTH,
} from '@tasama/utils';
import { i18n } from '@tasama/i18n';

/**
 * Authentication schemas for the portal app
 * Using zod and validation utilities from @tasama/utils
 * Messages are defined here for CMS integration
 */

// Password rules mirror the BFF DTO regex exactly:
// - Allowed charset: [A-Za-z0-9@$!%*?&] — any character outside this is rejected
// - Must contain at least one of: lowercase, uppercase, digit, special char (@$!%*?&)
const ALLOWED_CHARSET = /^[A-Za-z0-9@$!%*?&]+$/;
const SPECIAL_CHAR = /[@$!%*?&]/;

const createStrongPasswordSchema = (t: (_key: string) => string) =>
  z
    .string()
    .min(1, t('validation.password.required'))
    .min(PASSWORD_MIN_LENGTH, t('validation.password.minLength'))
    .max(PASSWORD_MAX_LENGTH, t('validation.password.maxLength'))
    .superRefine((password, ctx) => {
      if (!ALLOWED_CHARSET.test(password)) {
        ctx.addIssue({
          code: 'custom',
          message: t('validation.password.charset'),
        });
      }
      if (!/[a-z]/.test(password)) {
        ctx.addIssue({
          code: 'custom',
          message: t('validation.password.lowercase'),
        });
      }
      if (!/[A-Z]/.test(password)) {
        ctx.addIssue({
          code: 'custom',
          message: t('validation.password.uppercase'),
        });
      }
      if (!/[0-9]/.test(password)) {
        ctx.addIssue({
          code: 'custom',
          message: t('validation.password.number'),
        });
      }
      if (!SPECIAL_CHAR.test(password)) {
        ctx.addIssue({
          code: 'custom',
          message: t('validation.password.specialChar'),
        });
      }
    });

const getValidationMessages = () => ({
  email: {
    required: i18n.t('validation.email.required', { ns: 'auth' }),
    invalid: i18n.t('validation.email.invalid', { ns: 'auth' }),
    corporate: i18n.t('validation.email.corporate', { ns: 'auth' }),
  },
  password: {
    required: i18n.t('validation.password.required', { ns: 'auth' }),
    minLength: i18n.t('validation.password.minLength', { ns: 'auth' }),
  },
  resetToken: {
    required: i18n.t('validation.resetToken.required', { ns: 'auth' }),
  },
  confirmPassword: {
    required: i18n.t('validation.confirmPassword.required', { ns: 'auth' }),
    mismatch: i18n.t('validation.confirmPassword.mismatch', { ns: 'auth' }),
  },
});

/**
 * Create auth validation messages from i18n t() function.
 * Use this in components that have access to useTranslation.
 */
export const createAuthValidationMessages = (t: (_key: string) => string) => ({
  email: {
    required: t('validation.email.required'),
    invalid: t('validation.email.invalid'),
    corporate: t('validation.email.corporate'),
  },
  password: {
    required: t('validation.password.required'),
    minLength: t('validation.password.minLength'),
    maxLength: t('validation.password.maxLength'),
    charset: t('validation.password.charset'),
    lowercase: t('validation.password.lowercase'),
    uppercase: t('validation.password.uppercase'),
    number: t('validation.password.number'),
    specialChar: t('validation.password.specialChar'),
  },
  resetToken: {
    required: t('validation.resetToken.required'),
  },
  confirmPassword: {
    required: t('validation.confirmPassword.required'),
    mismatch: t('validation.confirmPassword.mismatch'),
  },
});

// Common validation schemas with portal-specific messages
export const portalValidations = {
  email: createEmailSchema({
    required: true,
    corporate: true,
    messages: getValidationMessages().email,
  }),
  basicEmail: createValidationSchema.email(getValidationMessages().email),
  password: createPasswordSchema({
    required: true,
    minLength: 8,
    messages: getValidationMessages().password,
  }),
  required: (message: string) => createValidationSchema.required(message),
  optional: createValidationSchema.optional,
};

/**
 * Create auth schemas with i18n validation messages.
 * Use this in components that have access to useTranslation.
 */
export const createAuthSchemas = (t: (_key: string) => string) => {
  const messages = createAuthValidationMessages(t);
  const strongPasswordSchema = createStrongPasswordSchema(t);

  return {
    login: z.object({
      email: createEmailSchema({
        required: true,
        corporate: true,
        messages: messages.email,
      }),
      password: createPasswordSchema({
        required: true,
        minLength: 8,
        messages: messages.password,
      }),
    }),
    forgotPassword: z.object({
      email: createEmailSchema({
        required: true,
        corporate: true,
        messages: messages.email,
      }),
    }),
    resetPassword: z
      .object({
        token: createValidationSchema.required(messages.resetToken.required),
        password: strongPasswordSchema,
        confirmPassword: createValidationSchema.required(
          messages.confirmPassword.required
        ),
      })
      .refine(
        (data) => {
          const { password, confirmPassword } = data as {
            password: string;
            confirmPassword: string;
          };
          return password === confirmPassword;
        },
        {
          error: messages.confirmPassword.mismatch,
          path: ['confirmPassword'],
        }
      ),
    accountActivation: z
      .object({
        email: createEmailSchema({
          required: true,
          corporate: true,
          messages: messages.email,
        }),
        password: strongPasswordSchema,
        confirmPassword: createValidationSchema.required(
          messages.confirmPassword.required
        ),
      })
      .refine(
        (data) => {
          const { password, confirmPassword } = data as {
            password: string;
            confirmPassword: string;
          };
          return password === confirmPassword;
        },
        {
          error: messages.confirmPassword.mismatch,
          path: ['confirmPassword'],
        }
      ),
  };
};

// Login form schema — basic validation only (verifying an existing password, not setting one)
export const loginSchema = z.object({
  email: portalValidations.email,
  password: portalValidations.password,
});

// Forgot password schema
export const forgotPasswordSchema = z.object({
  email: portalValidations.email,
});

// Reset password schema
export const resetPasswordSchema = z
  .object({
    token: portalValidations.required(
      getValidationMessages().resetToken.required
    ),
    password: strongPasswordSchema,
    confirmPassword: portalValidations.required(
      getValidationMessages().confirmPassword.required
    ),
  })
  .refine(
    (data) => {
      const { password, confirmPassword } = data as {
        password: string;
        confirmPassword: string;
      };
      return password === confirmPassword;
    },
    {
      error: getValidationMessages().confirmPassword.mismatch,
      path: ['confirmPassword'],
    }
  );

// Account activation/reset schema
export const accountActivationSchema = z
  .object({
    email: portalValidations.email,
    password: strongPasswordSchema,
    confirmPassword: portalValidations.required(
      getValidationMessages().confirmPassword.required
    ),
  })
  .refine(
    (data) => {
      const { password, confirmPassword } = data as {
        password: string;
        confirmPassword: string;
      };
      return password === confirmPassword;
    },
    {
      error: getValidationMessages().confirmPassword.mismatch,
      path: ['confirmPassword'],
    }
  );

// Type exports
export type LoginFormData = z.infer<typeof loginSchema>;
export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;
export type AccountActivationFormData = z.infer<typeof accountActivationSchema>;

// Types for i18n schemas
export type AuthSchemas = ReturnType<typeof createAuthSchemas>;
export type LoginFormDataI18n = z.infer<AuthSchemas['login']>;
export type ForgotPasswordFormDataI18n = z.infer<AuthSchemas['forgotPassword']>;
export type ResetPasswordFormDataI18n = z.infer<AuthSchemas['resetPassword']>;
export type AccountActivationFormDataI18n = z.infer<
  AuthSchemas['accountActivation']
>;

// User role types
export const USER_ROLES = {
  ADMIN: 'Admin',
  OPERATION_TEAM: 'Operation_Team',
} as const;

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

// API User type (as returned by the backend API)
export interface ApiUser {
  sub: string;
  username: string;
  email: string;
  isAdmin?: boolean; // Admin flag from backend
}

// Auth user type (internal app representation)
export interface AuthUser {
  id?: string;
  username: string;
  email: string;
  isAdmin: boolean; // Admin flag for quick admin checks
}

// Login/refresh response — tokens are set as httpOnly cookies by the BFF.
// Only user info and optional session fingerprint are returned in the body.
export interface LoginResponse {
  user: ApiUser;
  session_fingerprint?: string;
}

// Internal auth response type (mapped from API response)
export interface AuthResponse {
  user: AuthUser;
  sessionFingerprint?: string | undefined;
}

// Auth error type
export interface AuthError {
  message: string;
  code?: string | number;
  field?: string;
}

// New BFF endpoint interfaces
export interface SessionValidationRequest {
  session_fingerprint?: string; // Session ID from login response
  fingerprint?: string; // Client-generated fingerprint
}

export interface SessionInfoResponse {
  isValid: boolean;
  remainingTime: number; // Remaining session time in seconds
  expiresAt: string; // ISO timestamp of expiry
  user: {
    sub: string;
    username: string;
    email?: string;
    roles: UserRole[];
  };
  metadata: {
    lastActivity: string; // ISO timestamp
    canExtend: boolean; // Whether session can be extended
    fingerprintValid?: boolean; // NEW - indicates if fingerprint validation passed
  };
}

// ExtendSessionResponse removed - using refreshToken endpoint instead
// Session extension is now handled via the /auth/refresh endpoint

export interface LogoutAllRequest {
  session_fingerprint?: string; // Session ID for validation
  fingerprint?: string; // Client-generated fingerprint
}

export interface LogoutResponse {
  message: string;
  success: boolean;
}

export interface RefreshTokenRequest {
  session_fingerprint?: string;
  fingerprint?: string;
}

// Enhanced logout request interface
export interface LogoutRequest {
  session_fingerprint?: string;
  fingerprint?: string;
}

// Session extension request interface - REMOVED
// Backend /auth/extend-session endpoint has been removed
// Session extension is now handled via /auth/refresh endpoint

// Enhanced error codes for BFF integration
export const AUTH_ERROR_CODES = {
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  FINGERPRINT_MISMATCH: 'FINGERPRINT_MISMATCH',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  REFRESH_TOKEN_INVALID: 'REFRESH_TOKEN_INVALID',
} as const;

export interface StandardError {
  status: 'error';
  message: string;
  code: string;
  timestamp: string;
}
